import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Container, Paper, Button, Box, IconButton,
  Grid, Card, CardContent, FormControl, InputLabel,
  Select, MenuItem, Chip, Alert, CircularProgress, Divider, Tooltip,
} from '@mui/material';
import {
  CheckCircle, RadioButtonUnchecked,
  Delete, MeetingRoom, OpenInNew,
} from '@mui/icons-material';
import { cabinService, cabinAssignmentService, userService } from '../../services/api';
import TopBar from '../../components/TopBar';

const CabinAssignmentPage = () => {
  const navigate = useNavigate();
  // Use local date (not UTC) so the date matches what the server's DateTime.Today returns
  const _d = new Date();
  const today = `${_d.getFullYear()}-${String(_d.getMonth() + 1).padStart(2, '0')}-${String(_d.getDate()).padStart(2, '0')}`;

  const [cabins, setCabins] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selections, setSelections] = useState({});
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [cabinsRes, doctorsRes, assignmentsRes] = await Promise.all([
        cabinService.getAll(),
        userService.getDoctors(),
        cabinAssignmentService.getAssignments(today),
      ]);

      const activeCabins = cabinsRes.data.filter((c) => c.isActive);
      setCabins(activeCabins);
      setDoctors(doctorsRes.data);

      const todayAssignments = assignmentsRes.data;
      setAssignments(todayAssignments);

      const sel = {};
      todayAssignments.forEach((a) => { sel[a.cabinId] = a.doctorId; });
      setSelections(sel);

      setIsConfirmed(
        todayAssignments.length > 0 && todayAssignments.every((a) => a.isConfirmed)
      );
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load cabin assignment data.');
    } finally {
      setLoading(false);
    }
  }, [today]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDoctorSelect = async (cabinId, doctorId) => {
    setSelections((prev) => ({ ...prev, [cabinId]: doctorId }));
    if (!doctorId) {
      const existing = assignments.find((a) => a.cabinId === cabinId);
      if (existing) {
        await cabinAssignmentService.delete(existing.id);
        await loadData();
      }
      return;
    }
    try {
      await cabinAssignmentService.create({ cabinId, doctorId, assignmentDate: today });
      await loadData();
      setSuccess('Assignment saved.');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      console.error('Failed to save assignment:', err);
      setError('Failed to save assignment.');
    }
  };

  const handleConfirmAll = async () => {
    if (assignments.length === 0) {
      setError('Please assign at least one doctor to a cabin before confirming.');
      return;
    }
    setSaving(true);
    try {
      await cabinAssignmentService.confirmAll(today);
      await loadData();
      setSuccess('All assignments confirmed! The display screen is now live.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error('Failed to confirm:', err);
      setError('Failed to confirm assignments.');
    } finally {
      setSaving(false);
    }
  };

  const handleUnconfirmAll = async () => {
    setSaving(true);
    try {
      await cabinAssignmentService.unconfirmAll(today);
      await loadData();
      setSuccess('Assignments un-confirmed. Display screen is paused.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Failed to un-confirm:', err);
      setError('Failed to un-confirm assignments.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveAssignment = async (cabinId) => {
    const existing = assignments.find((a) => a.cabinId === cabinId);
    if (!existing) return;
    await cabinAssignmentService.delete(existing.id);
    setSelections((prev) => { const s = { ...prev }; delete s[cabinId]; return s; });
    await loadData();
  };

  const assignedCount = assignments.length;
  const dateLabel = new Date().toLocaleDateString('en-IN', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar
        title="Cabin Assignment"
        onBack={() => navigate('/admin/cabins')}
        actions={
          <>
            <Button
              color="inherit"
              startIcon={<OpenInNew />}
              onClick={() => window.open('/display', '_blank')}
              sx={{ mr: 1, border: '1px solid rgba(255,255,255,0.5)', borderRadius: 2, px: 2 }}
            >
              Display Screen
            </Button>
            {isConfirmed ? (
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<RadioButtonUnchecked />}
                onClick={handleUnconfirmAll}
                disabled={saving}
                sx={{ border: '1px solid rgba(255,255,255,0.7)', borderRadius: 2, px: 2 }}
              >
                Un-confirm
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<CheckCircle />}
                onClick={handleConfirmAll}
                disabled={saving || assignedCount === 0}
                sx={{ bgcolor: '#2e7d32', '&:hover': { bgcolor: '#1b5e20' }, borderRadius: 2, px: 2 }}
              >
                {saving ? 'Confirming…' : 'Confirm & Go Live'}
              </Button>
            )}
          </>
        }
      />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

        {/* Status Banner */}
        <Paper
          elevation={2}
          sx={{
            mb: 3, p: 2,
            backgroundColor: isConfirmed ? '#e8f5e9' : '#fff8e1',
            border: `1px solid ${isConfirmed ? '#a5d6a7' : '#ffe082'}`,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {isConfirmed
            ? <CheckCircle sx={{ color: '#2e7d32', fontSize: 28 }} />
            : <RadioButtonUnchecked sx={{ color: '#f57f17', fontSize: 28 }} />}
          <Typography fontWeight={600} color={isConfirmed ? '#1b5e20' : '#e65100'}>
            {isConfirmed
              ? `Display screen is LIVE — ${assignedCount} cabin(s) active today`
              : assignedCount === 0
                ? 'No assignments yet. Assign doctors to cabins below, then click "Confirm & Go Live".'
                : `${assignedCount} cabin(s) assigned — click "Confirm & Go Live" to activate the display screen.`}
          </Typography>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : cabins.length === 0 ? (
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
            <MeetingRoom sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary" gutterBottom>No active cabins found.</Typography>
            <Button variant="contained" onClick={() => navigate('/admin/cabins')}>
              Create Cabins First
            </Button>
          </Paper>
        ) : (
          <Grid container spacing={3}>
            {cabins.map((cabin) => {
              const assignment = assignments.find((a) => a.cabinId === cabin.id);
              const selectedDoctorId = selections[cabin.id] || '';
              const doctorObj = doctors.find((d) => d.id === (assignment?.doctorId));

              return (
                <Grid item xs={12} sm={6} md={4} lg={3} key={cabin.id}>
                  <Card
                    elevation={3}
                    sx={{
                      height: '100%',
                      borderRadius: 2,
                      border: assignment?.isConfirmed
                        ? '2px solid #4caf50'
                        : assignment
                          ? '2px solid #ff9800'
                          : '1px solid #e0e0e0',
                    }}
                  >
                    <CardContent>
                      {/* Cabin header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MeetingRoom color={assignment ? 'primary' : 'disabled'} />
                          <Typography variant="h6" fontWeight={700}>{cabin.name}</Typography>
                        </Box>
                        {assignment?.isConfirmed && (
                          <Chip label="Live" color="success" size="small" icon={<CheckCircle />} />
                        )}
                        {assignment && !assignment.isConfirmed && (
                          <Chip label="Assigned" color="warning" size="small" />
                        )}
                      </Box>

                      {cabin.description && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          {cabin.description}
                        </Typography>
                      )}

                      <Divider sx={{ my: 1.5 }} />

                      {/* Doctor selector */}
                      <FormControl fullWidth size="small">
                        <InputLabel>Assign Doctor</InputLabel>
                        <Select
                          value={selectedDoctorId}
                          label="Assign Doctor"
                          onChange={(e) => handleDoctorSelect(cabin.id, e.target.value)}
                          disabled={isConfirmed}
                        >
                          <MenuItem value=""><em>— Not Assigned —</em></MenuItem>
                          {doctors.map((doc) => (
                            <MenuItem key={doc.id} value={doc.id}>
                              Dr. {doc.firstName} {doc.lastName}
                              {doc.specialization ? ` (${doc.specialization})` : ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Doctor info box */}
                      {doctorObj && (
                        <Box sx={{ mt: 1.5, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                          <Typography variant="body2" fontWeight={600}>
                            Dr. {doctorObj.firstName} {doctorObj.lastName}
                          </Typography>
                          {doctorObj.specialization && (
                            <Typography variant="caption" color="text.secondary">
                              {doctorObj.specialization}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {/* Remove button */}
                      {assignment && !isConfirmed && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                          <Tooltip title="Remove assignment">
                            <IconButton size="small" color="error" onClick={() => handleRemoveAssignment(cabin.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Container>
    </div>
  );
};

export default CabinAssignmentPage;
